import React, { useEffect, useState } from "react";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { createPortal } from "react-dom";
import { devDebug } from "../lib/devLog";
import {
  Mic,
  Play,
  MoreHorizontal,
  ChevronRight,
  Languages,
  BadgeCheck,
  Send,
  X,
  Trash2,
  UserPlus,
  UserCheck,
  Copy,
  ExternalLink,
} from "lucide-react";
import { Waveform } from "./Waveform";
import { postProgress } from "../lib/waveform";
import { POST_ACTIONS } from "../data/constants";
import { fetchReplies, publishVoicePost, reactToPost, deletePost, diffusePost, translateVoicePost } from "../lib/postsApi";
import { toggleFollow } from "../lib/socialApi";
import { useVoiceRecorder } from "../hooks/useVoiceRecorder";
import { VoicePresetPicker, voicePresetHintText, voicePresetLabel } from "./VoicePresetPicker";
import { PermissionRecoveryDialog } from "./PermissionRecoveryDialog";
import { loadIdentity } from "../lib/identity";
import { useMiniProfile } from "../lib/miniProfileContext";
import { ExternalVideoPreview } from "./ExternalVideoPreview";

const API = process.env.REACT_APP_BACKEND_URL || "";
const NativeSpeech = registerPlugin("NativeSpeech");

function PostHeader({ author, time, verified, identityId }) {
  const { open } = useMiniProfile();
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button
        type="button"
        onClick={() => identityId && open(identityId)}
        disabled={!identityId}
        className="font-semibold text-[15px] text-ink hover:text-primary hover:underline disabled:hover:no-underline disabled:hover:text-ink"
        data-testid={identityId ? `author-open-${identityId}` : "author-open"}
        title={identityId ? "Apri profilo" : ""}
      >
        {author}
      </button>
      <span className="text-xs text-muted">{time}</span>
      {verified && (
        <span className="chip">
          <BadgeCheck size={11} strokeWidth={2.5} /> Fonte verificata
        </span>
      )}
    </div>
  );
}

function VoiceCommentControl({ postId, active, onToggle }) {
  return (
    <div className="inline-flex items-center gap-2">
      <button
        type="button"
        className={`mic-btn ${active ? "!bg-ink" : ""}`}
        onClick={onToggle}
        data-testid={`reply-voice-${postId}`}
        aria-label={active ? "Chiudi commento vocale" : "Commenta a voce"}
        aria-expanded={active}
      >
        {active ? <X size={20} /> : <Mic size={20} />}
      </button>
      <span className="text-xs font-semibold text-ink">Commenta</span>
    </div>
  );
}

function PostMedia({ postId, attachments = [], canComment, commenting, onComment }) {
  const commentControl = canComment ? (
    <VoiceCommentControl postId={postId} active={commenting} onToggle={onComment} />
  ) : null;

  if (!attachments.length) {
    return commentControl ? <div className="mt-3">{commentControl}</div> : null;
  }
  return (
    <div className="mt-3">
      <div className="grid grid-cols-3 gap-2" data-testid={`post-attachments-${postId}`}>
        {attachments.map((item) => {
          if (item.moderationStatus !== "approved") {
            const pending = item.moderationStatus === "pending";
            const message = pending
              ? "Allegato in revisione"
              : item.moderationStatus === "rejected"
                ? "Allegato non approvato dalla moderazione"
                : "Video non elaborabile";
            return (
              <div
                key={item.id}
                className={`aspect-video rounded-xl border flex flex-col items-center justify-center gap-1 p-2 text-center text-[11px] ${pending ? "border-amber-300 bg-amber-50 text-amber-800" : "border-primary/30 bg-primary/5 text-primary"}`}
                role="status"
              >
                <strong>{message}</strong>
                {!pending && item.moderationReason && (
                  <span className="text-[10px] leading-tight">{item.moderationReason}</span>
                )}
              </div>
            );
          }
          if (item.kind === "image" && item.objectKey) {
            return <img key={item.id} src={`${API}/api/v1/uploads/audio/${item.objectKey}`} alt="" className="w-full aspect-video object-cover rounded-xl border border-edge" />;
          }
          if (item.kind === "video" && item.objectKey) {
            return <video key={item.id} src={`${API}/api/v1/uploads/audio/${item.objectKey}`} controls className="w-full aspect-video object-cover rounded-xl border border-edge" />;
          }
          if (item.kind === "video" && item.externalUrl) {
            return <div key={item.id} className="aspect-video overflow-hidden rounded-xl border border-edge bg-cream"><ExternalVideoPreview url={item.externalUrl} provider={item.provider} /></div>;
          }
          return null;
        })}
      </div>
      {commentControl && <div className="mt-2">{commentControl}</div>}
    </div>
  );
}

function PostPlayer({ post, onTranslate }) {
  const translateButton = post.audioObjectKey ? (
    <button
      type="button"
      onClick={onTranslate}
      className="h-9 w-9 shrink-0 rounded-full border-2 border-primary bg-white text-primary flex items-center justify-center hover:bg-primary hover:text-white"
      aria-label="Traduci messaggio vocale"
      title="Traduci"
      data-testid={`translate-audio-${post.id}`}
    >
      <Languages size={18} strokeWidth={2.5} />
    </button>
  ) : null;
  if (post.audioUrl) {
    return (
      <div className="mt-3 flex items-center gap-2 bg-cream rounded-full pl-2 pr-2 py-1.5">
        <audio
          src={post.audioUrl}
          controls
          className="flex-1 min-w-0 h-8"
          data-testid={`audio-${post.id}`}
        />
        {translateButton}
      </div>
    );
  }
  return (
    <div className="mt-3 flex items-center gap-2 bg-cream rounded-full pl-2 pr-2 py-1.5">
      <button
        className="w-8 h-8 rounded-full bg-white border border-edge flex items-center justify-center text-ink hover:bg-ink hover:text-white"
        data-testid={`play-${post.id}`}
        aria-label="Riproduci"
      >
        <Play size={12} />
      </button>
      <Waveform id={post.id} progress={postProgress(post.id)} />
      <span className="text-xs text-muted tabular-nums shrink-0">{post.duration}</span>
      {translateButton}
    </div>
  );
}

function PostActions({ post }) {
  const [reactions, setReactions] = useState(post.reactions);
  const [following, setFollowing] = useState(!!post.followedByMe);
  const [busy, setBusy] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [notice, setNotice] = useState("");
  const me = loadIdentity();
  const canReact = post.source === "live";
  const isSelf = me?.identityId === post.identityId;

  const flash = (message) => {
    setNotice(message);
    window.setTimeout(() => setNotice(""), 2200);
  };
  const canonicalUrl = () => {
    const url = new URL(window.location.origin);
    url.searchParams.set("post", post.id);
    if (post.topicSlug) url.searchParams.set("topic", post.topicSlug);
    return url.toString();
  };
  const shareText = () => `${post.author}: ${post.text || "Messaggio vocale su Opinione Libera"}`;

  const doReact = async (kind) => {
    if (!canReact || busy) return;
    setBusy(true);
    try {
      const next = await reactToPost(post.id, kind);
      if (next) setReactions(next);
    } catch (err) {
      devDebug("VoicePost: reaction failed", err);
    } finally {
      setBusy(false);
    }
  };

  const doFollow = async () => {
    if (!canReact || isSelf || busy) return;
    setBusy(true);
    try {
      const next = await toggleFollow(post.identityId, following ? "unfollow" : "follow");
      setFollowing(next.following);
      flash(next.following ? "Ora riceverai le nuove pubblicazioni" : "Non segui più questo utente");
    } catch (err) {
      devDebug("VoicePost: follow toggle failed", err);
    } finally {
      setBusy(false);
    }
  };

  const doDiffuse = async () => {
    if (!canReact || busy) return;
    setBusy(true);
    try {
      await diffusePost(post.id);
      flash("Messaggio diffuso nella tua rete");
    } catch (err) {
      devDebug("VoicePost: diffusion failed", err);
      flash("Impossibile diffondere il messaggio");
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(canonicalUrl());
    flash("Link copiato");
    setShareOpen(false);
  };
  const nativeShare = async () => {
    if (navigator.share) {
      await navigator.share({ title: "Opinione Libera", text: shareText(), url: canonicalUrl() });
      setShareOpen(false);
    } else {
      await copyLink();
    }
  };
  const openExternal = (provider) => {
    const url = encodeURIComponent(canonicalUrl());
    const text = encodeURIComponent(shareText());
    const target = provider === "whatsapp"
      ? `https://wa.me/?text=${text}%20${url}`
      : `https://www.facebook.com/sharer/sharer.php?u=${url}`;
    window.open(target, "_blank", "noopener,noreferrer");
    setShareOpen(false);
  };

  return (
    <div className="relative">
      <div className="mt-2.5 flex items-center gap-1 flex-wrap">
        {POST_ACTIONS.map((a) => {
          const Icon = a.icon;
          let count = null;
          if (a.key === "like") count = reactions?.like;
          else if (a.key === "dislike") count = reactions?.dislike;
          const onClick =
            a.key === "like" || a.key === "dislike" ? () => doReact(a.key)
            : a.key === "share" ? () => setShareOpen((v) => !v)
            : a.key === "diffuse" ? doDiffuse
            : undefined;
          return (
            <button
              key={`${post.id}-${a.key}`}
              className={`action-btn ${a.iconOnly ? "!w-9 !h-9 !p-0 !justify-center" : ""}`}
              data-testid={a.testable ? `${a.key}-${post.id}` : `${a.key}-${post.id}`}
              aria-label={a.label}
              title={a.label}
              onClick={onClick}
              disabled={(a.key === "like" || a.key === "dislike" || a.key === "diffuse") && !canReact}
            >
              <Icon size={14} /> {!a.iconOnly && a.label}
              {count != null && count > 0 && (
                <span className="ml-1 text-[10px] font-bold text-primary tabular-nums">{count}</span>
              )}
            </button>
          );
        })}
        {!isSelf && (
          <button
            className={`action-btn ${following ? "!text-primary !font-semibold" : ""}`}
            onClick={doFollow}
            disabled={!canReact || busy}
            data-testid={`follow-${post.id}`}
          >
            {following ? <UserCheck size={14} /> : <UserPlus size={14} />}
            {following ? "Smetti di seguire" : "Segui"}
          </button>
        )}
      </div>

      {shareOpen && (
        <div className="mt-2 w-full sm:w-80 rounded-2xl border border-edge bg-white p-3 shadow-lg z-20" data-testid={`share-menu-${post.id}`}>
          <p className="text-xs font-semibold text-ink mb-2">Condividi questo messaggio</p>
          <div className="grid grid-cols-2 gap-2">
            <button className="action-btn justify-center" onClick={nativeShare}><ExternalLink size={14} /> Condividi</button>
            <button className="action-btn justify-center" onClick={copyLink}><Copy size={14} /> Copia link</button>
            <button className="action-btn justify-center" onClick={() => openExternal("whatsapp")}>WhatsApp</button>
            <button className="action-btn justify-center" onClick={() => openExternal("facebook")}>Facebook</button>
          </div>
          <p className="text-[11px] text-muted mt-2">“Diffondi” pubblica invece il messaggio nella rete interna di Opinione Libera.</p>
        </div>
      )}
      {notice && <p className="text-xs text-primary font-semibold mt-2" role="status">{notice}</p>}
    </div>
  );
}

function PostSide({ post, onTranslate, onDelete, deleting }) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  return (
    <div className="relative w-full sm:w-auto flex flex-row sm:flex-col items-center sm:items-end justify-end gap-2 shrink-0 pl-12 sm:pl-0">
      <button
        type="button"
        className="text-muted p-2 hover:text-ink rounded-full hover:bg-cream"
        aria-label="Altre azioni"
        aria-expanded={open}
        onClick={() => { setOpen((value) => !value); setConfirming(false); }}
        data-testid={`post-menu-${post.id}`}
      >
        <MoreHorizontal size={18} />
      </button>
      {open && (
        <div className="absolute right-0 top-10 z-50 w-56 rounded-2xl border-2 border-ink bg-cream p-2 shadow-2xl" role="menu">
          <div className="flex items-center justify-between px-2 py-1 border-b border-edge mb-1">
            <strong className="text-sm">Azioni messaggio</strong>
            <button type="button" onClick={() => setOpen(false)} className="h-7 w-7 rounded-full bg-white border border-edge flex items-center justify-center" aria-label="Chiudi"><X size={14}/></button>
          </div>
          {post.audioObjectKey && (
            <button type="button" className="w-full rounded-xl px-3 py-2.5 text-left text-sm font-semibold hover:bg-white flex items-center gap-2" onClick={() => { setOpen(false); onTranslate(); }} role="menuitem">
              <Languages size={16} className="text-primary"/> Traduci
            </button>
          )}
          {post.isMine && !confirming && (
            <button type="button" className="w-full rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-primary hover:bg-white flex items-center gap-2" onClick={() => setConfirming(true)} role="menuitem" data-testid={`delete-${post.id}`}>
              <Trash2 size={16}/> Elimina
            </button>
          )}
          {post.isMine && confirming && (
            <div className="rounded-xl bg-white border border-edge p-2">
              <p className="text-xs font-semibold mb-2">Eliminare definitivamente questo messaggio?</p>
              <div className="flex gap-2">
                <button type="button" disabled={deleting} onClick={onDelete} className="flex-1 rounded-lg bg-primary text-white px-2 py-2 text-xs font-bold disabled:opacity-50" data-testid={`confirm-delete-${post.id}`}>{deleting ? "Elimino…" : "Elimina"}</button>
                <button type="button" onClick={() => setConfirming(false)} className="rounded-lg border border-edge px-2 py-2 text-xs">Annulla</button>
              </div>
            </div>
          )}
          {post.isMine && (
            <button type="button" className="w-full rounded-xl px-3 py-2.5 text-left text-sm hover:bg-white flex items-center gap-2" role="menuitem">
              <ChevronRight size={15}/> Prosegui
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const TRANSLATION_LANGUAGES = [
  ["it", "Italiano"],
  ["en", "English"],
  ["es", "Español"],
  ["fr", "Français"],
  ["de", "Deutsch"],
  ["pt", "Português"],
];

function deviceLanguage() {
  try {
    const preferred = window.localStorage.getItem("ol-preferred-language");
    if (TRANSLATION_LANGUAGES.some(([item]) => item === preferred)) return preferred;
  } catch {}
  const code = String(navigator.language || "it").toLowerCase().split("-")[0];
  return TRANSLATION_LANGUAGES.some(([item]) => item === code) ? code : "it";
}

async function speakTranslatedText(text, language) {
  if (!text?.trim()) throw new Error("La traduzione non contiene testo da riprodurre");

  if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android") {
    await NativeSpeech.speak({ text, language });
    return;
  }

  if (!("speechSynthesis" in window) || typeof SpeechSynthesisUtterance === "undefined") {
    throw new Error("Sintesi vocale non disponibile su questo dispositivo");
  }

  await new Promise((resolve, reject) => {
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = language;
    utterance.onstart = resolve;
    utterance.onerror = (event) => reject(
      new Error(event?.error ? `Riproduzione non riuscita: ${event.error}` : "Riproduzione non riuscita"),
    );
    window.speechSynthesis.speak(utterance);
  });
}

function TranslationDialog({ post, open, onClose }) {
  const countryLanguage = deviceLanguage();
  const [target, setTarget] = useState(countryLanguage);
  const [working, setWorking] = useState(false);
  const [translatedText, setTranslatedText] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setTarget(deviceLanguage());
    setTranslatedText("");
    setError("");
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const closeOnEscape = (event) => { if (event.key === "Escape" && !working) onClose(); };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [open, onClose, working]);

  const translate = async (language) => {
    if (!post.audioObjectKey || working) return;
    setWorking(true);
    setError("");
    try {
      const result = await translateVoicePost({
        postId: post.id,
        audioObjectKey: post.audioObjectKey,
        targetLanguage: language,
      });
      const text = String(result?.translatedText || "").trim();
      if (!text) throw new Error("La traduzione non contiene testo");
      setTranslatedText(text);
      await speakTranslatedText(text, language);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Traduzione non disponibile");
    } finally {
      setWorking(false);
    }
  };

  const listenAgain = async () => {
    if (!translatedText || working) return;
    setWorking(true);
    setError("");
    try {
      await speakTranslatedText(translatedText, target);
    } catch (err) {
      setError(err?.message || "Riproduzione non disponibile");
    } finally {
      setWorking(false);
    }
  };

  if (!open || !post.audioObjectKey) return null;
  const countryLabel = TRANSLATION_LANGUAGES.find(([code]) => code === countryLanguage)?.[1] || "Italiano";
  return createPortal(
    <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4">
      <button type="button" className="absolute inset-0 bg-ink/60 backdrop-blur-sm" onClick={() => !working && onClose()} aria-label="Chiudi traduzione"/>
      <section className="relative z-[1201] w-[min(92vw,400px)] max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-3xl border-2 border-ink bg-cream p-4 shadow-2xl" role="dialog" aria-modal="true" aria-labelledby={`translation-title-${post.id}`}>
        <header className="flex items-center gap-3 border-b border-edge pb-3">
          <span className="h-10 w-10 rounded-full bg-primary text-white flex items-center justify-center"><Languages size={21}/></span>
          <div><p className="text-[10px] uppercase tracking-[0.15em] text-primary font-bold">Opinione-Libera</p><h2 id={`translation-title-${post.id}`} className="text-xl font-bold">Traduci messaggio</h2></div>
          <button type="button" disabled={working} onClick={onClose} className="ml-auto h-10 w-10 rounded-full border border-edge bg-white flex items-center justify-center disabled:opacity-40" aria-label="Chiudi"><X size={19}/></button>
        </header>
        <button type="button" disabled={working} onClick={() => translate(countryLanguage)} className="mt-3 w-full rounded-2xl bg-primary px-4 py-3 text-left text-white disabled:opacity-50">
          <span className="block text-sm font-bold">Traduci nella lingua di questo paese</span>
          <small>{countryLabel} · rilevata dal dispositivo</small>
        </button>
        <label className="mt-4 block text-xs font-bold uppercase tracking-wider text-muted" htmlFor={`translation-language-${post.id}`}>Imposta lingua</label>
        <select id={`translation-language-${post.id}`} value={target} onChange={(event) => setTarget(event.target.value)} disabled={working} className="mt-2 w-full min-h-12 rounded-2xl border-2 border-ink bg-white px-4 text-base">
          {TRANSLATION_LANGUAGES.map(([code,label]) => <option key={code} value={code}>{label}</option>)}
        </select>
        <button type="button" disabled={working} onClick={() => translate(target)} className="mt-3 w-full rounded-2xl bg-ink px-4 py-3 text-sm font-bold text-white disabled:opacity-50">
          {working ? "Traduzione e ascolto in corso…" : "OK · Traduci e ascolta"}
        </button>
        {translatedText && (
          <div className="mt-3 rounded-2xl border border-edge bg-white p-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-primary">Testo tradotto</p>
            <p className="mt-1 text-sm leading-5 text-ink">{translatedText}</p>
            <button type="button" disabled={working} onClick={listenAgain} className="mt-3 inline-flex items-center gap-2 rounded-full border-2 border-ink px-3 py-2 text-xs font-bold disabled:opacity-50">
              <Play size={13} /> Ascolta di nuovo
            </button>
          </div>
        )}
        {error && <p className="mt-3 rounded-xl border border-primary/30 bg-white p-3 text-xs text-primary" role="alert">{error}</p>}
      </section>
    </div>,
    document.body,
  );
}

function ReplyComposer({ topicSlug, postId, onCancel, onReplied }) {
  const rec = useVoiceRecorder();
  const [caption, setCaption] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState(null);

  const toggle = () => { if (rec.recording) rec.stop(); else void rec.start(); };
  const send = async () => {
    if (!rec.blobUrl || sending) return;
    setSending(true);
    setErr(null);
    try {
      const blobResp = await fetch(rec.blobUrl);
      const blob = await blobResp.blob();
      await publishVoicePost({
        topicSlug,
        durationMs: rec.durationMs,
        blob,
        caption,
        replyToPostId: postId,
        voiceModified: rec.preset !== "off",
      });
      rec.reset();
      setCaption("");
      onReplied();
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || "Errore");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-3 pl-4 border-l-2 border-primary/30" data-testid={`reply-composer-${postId}`}>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <button
          onClick={toggle}
          className={`mic-btn ${rec.recording ? "!bg-ink animate-pulse" : ""}`}
          data-testid={`reply-mic-${postId}`}
          aria-label="Registra commento"
        >
          <Mic size={16} />
        </button>
        <span className="text-xs tabular-nums text-muted">
          {String(Math.floor(rec.seconds / 60)).padStart(2, "0")}:{String(rec.seconds % 60).padStart(2, "0")}
        </span>
        {rec.blobUrl && !rec.recording && (
          <audio src={rec.blobUrl} controls className="flex-1 h-8 min-w-0" />
        )}
        <VoicePresetPicker
          value={rec.preset}
          disabled={rec.recording || rec.processing || !!rec.blobUrl}
          onChange={rec.setPreset}
          testId={`reply-voice-preset-${postId}`}
        />
        <button
          onClick={onCancel}
          className="text-muted hover:text-primary p-1"
          data-testid={`reply-cancel-${postId}`}
          aria-label="Annulla"
        >
          <X size={14} />
        </button>
      </div>
      {rec.processing && (
        <div className="text-xs text-muted mb-2" data-testid={`reply-processing-${postId}`}>
          Sto applicando l'effetto {voicePresetLabel(rec.preset).toLowerCase()}…
        </div>
      )}
      {rec.preset !== "off" && !rec.recording && !rec.blobUrl && !rec.processing && (
        <div className="text-xs text-muted mb-2" data-testid={`reply-preset-hint-${postId}`}>
          Camuffatore: <strong>{voicePresetLabel(rec.preset)}</strong>{" · "}
          {voicePresetHintText(rec.preset)}
        </div>
      )}
      <PermissionRecoveryDialog
        issue={rec.permissionIssue}
        onClose={rec.clearPermissionIssue}
        onRetry={rec.start}
      />
      {rec.error && <div className="text-xs text-primary mb-1">{rec.error}</div>}
      {rec.blobUrl && !rec.recording && (
        <input
          type="text"
          value={caption}
          onChange={(e) => setCaption(e.target.value.slice(0, 240))}
          placeholder="Aggiungi una didascalia (opzionale)"
          className="w-full px-3 py-1.5 rounded-full border border-edge bg-white text-xs outline-none mb-2"
          data-testid={`reply-caption-${postId}`}
        />
      )}
      {err && <div className="text-xs text-primary mb-1">{err}</div>}
      <button
        onClick={send}
        disabled={!rec.blobUrl || rec.recording || rec.processing || sending}
        className="brutal-send text-xs disabled:opacity-40"
        data-testid={`reply-send-${postId}`}
      >
        <Send size={12} /> {sending ? "Invio…" : "Pubblica commento"}
      </button>
    </div>
  );
}

function ReplyList({ postId, refreshKey }) {
  const [items, setItems] = useState([]);
  const [translationReply, setTranslationReply] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetchReplies(postId)
      .then((list) => { if (!cancelled) setItems(list); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [postId, refreshKey]);
  if (!items.length) return null;
  return (
    <div className="mt-3 pl-4 border-l-2 border-edge space-y-3" data-testid={`replies-${postId}`}>
      {items.map((r) => (
        <div key={r.id} className="flex items-start gap-2" data-testid={`reply-${r.id}`}>
          <img
            src={r.avatar || `https://api.dicebear.com/9.x/thumbs/svg?seed=${r.identityId}`}
            alt=""
            className="w-7 h-7 rounded-full border border-edge object-cover"
          />
          <div className="flex-1 min-w-0">
            <div className="text-xs">
              <span className="font-semibold text-ink">{r.displayName || `Utente ${r.identityId.slice(0, 6)}`}</span>
              <span className="text-muted ml-2 text-[11px]">{new Date(r.publishedAt).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</span>
            </div>
            {r.caption && <p className="text-[13px] text-ink mt-0.5">{r.caption}</p>}
            {r.audioObjectKey && (
              <div className="mt-1 flex items-center gap-1.5 rounded-full bg-cream px-1.5 py-1">
                <audio
                  src={`${API}/api/v1/uploads/audio/${r.audioObjectKey}`}
                  controls
                  className="min-w-0 flex-1 h-7"
                  data-testid={`reply-audio-${r.id}`}
                />
                <button
                  type="button"
                  onClick={() => setTranslationReply(r)}
                  className="h-7 w-7 shrink-0 rounded-full border border-primary bg-white text-primary flex items-center justify-center hover:bg-primary hover:text-white"
                  aria-label="Traduci commento vocale"
                  title="Traduci e ascolta"
                  data-testid={`translate-reply-audio-${r.id}`}
                >
                  <Languages size={14} strokeWidth={2.5} />
                </button>
              </div>
            )}
          </div>
        </div>
      ))}
      <TranslationDialog
        post={translationReply}
        open={!!translationReply}
        onClose={() => setTranslationReply(null)}
      />
    </div>
  );
}

export function VoicePost({ post, topicSlug, onDeleted, onOpenMessages }) {
  const [replying, setReplying] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const [translationOpen, setTranslationOpen] = useState(false);
  const bumpReplies = () => { setRefreshKey((k) => k + 1); setReplying(false); };
  const canReply = post.source === "live";
  const doDelete = async () => {
    if (!post.isMine || deleting) return;
    setDeleting(true);
    try {
      await deletePost(post.id);
      if (typeof onDeleted === "function") onDeleted();
    } catch {
      setDeleting(false);
    }
  };
  return (
    <div className="py-4 sm:py-5 border-b border-edgeSoft overflow-visible" data-testid={`post-${post.id}`}>
      <div className="flex flex-wrap sm:flex-nowrap items-start gap-3">
        <img
          src={post.avatar}
          alt={post.author}
          className="w-10 h-10 rounded-full object-cover border border-edge"
          onError={(e) => (e.currentTarget.style.visibility = "hidden")}
        />
        <div className="flex-1 min-w-0">
          <PostHeader author={post.author} time={post.time} verified={post.verified} identityId={post.identityId} />
          <p className="text-[14px] text-ink mt-1 leading-relaxed">{post.text}</p>
          <PostMedia
            postId={post.id}
            attachments={post.attachments}
            canComment={canReply}
            commenting={replying}
            onComment={() => setReplying((value) => !value)}
          />
          <PostPlayer post={post} onTranslate={() => setTranslationOpen(true)} />
          <PostActions post={post} onOpenMessages={onOpenMessages} />
          {replying && (
            <ReplyComposer
              topicSlug={topicSlug}
              postId={post.id}
              onCancel={() => setReplying(false)}
              onReplied={bumpReplies}
            />
          )}
          {canReply && <ReplyList postId={post.id} refreshKey={refreshKey} />}
        </div>
        <PostSide
          post={post}
          onTranslate={() => setTranslationOpen(true)}
          onDelete={doDelete}
          deleting={deleting}
        />
      </div>
      <TranslationDialog post={post} open={translationOpen} onClose={() => setTranslationOpen(false)} />
    </div>
  );
}
